/**
 * Update Prompt Component
 * 
 * BUG FIX v17: Shows a user-friendly banner when a new service worker update is available
 * Allows users to update the app with a single tap instead of requiring manual refresh
 */

import { useEffect, useState } from 'react';

interface UpdatePromptProps {
  onUpdate: () => void;
  onDismiss?: () => void;
}

export function UpdatePrompt({ onUpdate, onDismiss }: UpdatePromptProps) {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-dismiss after 30 seconds if user doesn't interact
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onDismiss) {
        setIsVisible(false);
        onDismiss();
      }
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [onDismiss]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed left-1/2 z-50 -translate-x-1/2 transform transition-all duration-300 ease-in-out"
      role="alert"
      aria-live="polite"
      style={{
        animation: 'slideUp 0.3s ease-out',
        width: '100%',
        maxWidth: '400px',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)', // Above bottom nav (56px nav + 16px padding)
      }}
    >
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
      <div className="rounded-lg border border-[#007c86] bg-white px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Update available</p>
            <p className="text-xs text-gray-600 mt-0.5">A new version is ready. Refresh to get the latest features.</p>
          </div>
          <div className="flex items-center gap-2">
            {onDismiss && (
              <button
                onClick={() => {
                  setIsVisible(false);
                  onDismiss();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss update prompt"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              onClick={onUpdate}
              className="rounded-md bg-[#007c86] px-4 py-2 text-sm font-medium text-white hover:bg-[#006a73] transition-colors focus:outline-none focus:ring-2 focus:ring-[#007c86] focus:ring-offset-2"
              aria-label="Refresh to update"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

